import React, { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { PrinterIcon } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  COMPLIANCE_META,
  isComplianceStatsFocus,
} from './complianceFocus';
import {
  bucketMixFromCountRows,
  buildComplianceCountRows,
  COMPLIANCE_COUNT_SPEC,
  dim2MixFromCountRows,
  type ComplianceCountRow,
} from './complianceCount';
import { ReportTextCell, CrewDataTableFrame, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { complianceCountPrintPath } from './reports/complianceCountPrintMeta';
import {
  formatStatNum,
  StatsBucketChart,
  StatsBucketHeader,
  StatsCountCell,
  StatsKpiListLayout,
  StatsMixPanel,
  StatsPageShell,
  StatsRankedBars,
} from './statisticsShared';
import type {
  CrewStatsCertRow,
  CrewStatsMedicalRow,
} from './types';

const CrewNameLink: React.FC<{ crewId: number; name: string }> = ({ crewId, name }) => {
  const { colors } = useTheme();
  const { listReturnPath } = useCrewReportOpen();
  return (
    <Link
      to={`/crew/${crewId}`}
      state={{ from: listReturnPath }}
      className={cn('text-sm hover:underline', colors.primaryFg)}
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  );
};

const ComplianceCountStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const focus = isComplianceStatsFocus(focusParam) ? focusParam : null;
  const meta = focus ? COMPLIANCE_META[focus] : null;
  const spec = focus ? COMPLIANCE_COUNT_SPEC[focus] : null;
  const [listTab, setListTab] = useState<'summary' | 'detail'>('summary');

  const summaryColumns: Column<ComplianceCountRow>[] = useMemo(() => {
    if (!spec) return [];
    const bucketCols: Column<ComplianceCountRow>[] = spec.buckets.map((b) => ({
      key: b.key,
      header: <StatsBucketHeader colorClass={b.bar} label={b.label} />,
      sortable: true,
      align: 'right' as const,
      footerType: 'sum' as const,
      columnGroup: { id: 'buckets', header: spec.bucketGroup },
      render: (v: unknown) => <StatsCountCell value={v} emphasize={b.emphasize} />,
    }));
    return [
      {
        key: 'dim1',
        header: spec.dim1Header,
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      {
        key: 'dim2',
        header: spec.dim2Header,
        sortable: true,
        render: (v: string) => <ReportTextCell value={v} />,
      },
      ...bucketCols,
      {
        key: 'unknown',
        header: <StatsBucketHeader colorClass="bg-slate-400" label={spec.unknownLabel} />,
        sortable: true,
        align: 'right',
        footerType: 'sum',
        columnGroup: { id: 'buckets', header: spec.bucketGroup },
        render: (v: unknown) => <StatsCountCell value={v} />,
      },
      {
        key: 'total',
        header: 'Total',
        sortable: true,
        align: 'right',
        footerType: 'sum',
        render: (v: number) => (
          <span className={cn('tabular-nums font-semibold', colors.text)}>{v}</span>
        ),
      },
    ];
  }, [colors.text, spec]);

  const certColumns: Column<CrewStatsCertRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => <CrewNameLink crewId={row.crewId} name={row.name} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certificate', header: 'Certificate', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'category', header: 'Category', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'expiredDate',
        header: 'Expiry',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      { key: 'daysUntilExpiry', header: 'Days', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const medicalColumns: Column<CrewStatsMedicalRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => <CrewNameLink crewId={row.crewId} name={row.name} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'kind', header: 'Kind', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'nextDueDate',
        header: 'Next due',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      { key: 'daysUntilExpiry', header: 'Days', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  if (!focus || !meta || !spec) {
    return <Navigate to="/crew/statistics/compliance/certs-expired" replace />;
  }

  const barByLabel: Record<string, string> = Object.fromEntries([
    ...spec.buckets.map((b) => [b.label, b.bar]),
    ['Unknown', 'bg-slate-400'],
  ]);

  return (
    <StatsPageShell
      hintTitle={meta.title}
      hintBody={meta.statsBody}
      filterMode="vessel-days"
      trailingActions={({ applied }) =>
        applied ? (
          <Link
            to={complianceCountPrintPath(focus, {
              vesselId: applied.vesselId,
              withinDays: applied.withinDays,
            })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        ) : null
      }
    >
      {({ data }) => {
        const lists = data.lists;
        const certs =
          focus === 'certs-expired' ? lists?.certsExpired ?? [] : lists?.certsExpiring ?? [];
        const medicals =
          focus === 'medicals-expired' ? lists?.medicalsExpired ?? [] : lists?.medicalsExpiring ?? [];

        const detailCount = (() => {
          switch (focus) {
            case 'certs-expired':
            case 'certs-expiring':
              return certs.length;
            case 'medicals-expired':
            case 'medicals-due':
              return medicals.length;
          }
        })();

        const rows = buildComplianceCountRows(focus, { certs, medicals });
        const dim1Count = new Set(rows.map((r) => r.dim1)).size;
        const dim2Count = new Set(rows.map((r) => r.dim2)).size;
        const showUnknown = rows.some((r) => r.unknown > 0);
        const columns = showUnknown
          ? summaryColumns
          : summaryColumns.filter((c) => c.key !== 'unknown');
        const bucketMix = bucketMixFromCountRows(rows, spec.buckets);
        const mix = dim2MixFromCountRows(rows);
        const empty = meta.empty;

        return (
          <StatsKpiListLayout
            charts={
              <StatsMixPanel
                metrics={[
                  { label: spec.metricLabel, value: formatStatNum(detailCount) },
                  { label: spec.dim1Header, value: formatStatNum(dim1Count) },
                  { label: spec.dim2Header, value: formatStatNum(dim2Count) },
                ]}
              >
                <StatsBucketChart
                  title={spec.chartTitle}
                  hint={spec.chartHint}
                  data={bucketMix}
                  total={detailCount}
                  emptyLabel={empty}
                  barClassFor={(label) => barByLabel[label] ?? 'bg-slate-400'}
                />
                <div className={cn('shrink-0 border-t', colors.border)} />
                <StatsRankedBars
                  title={spec.mixTitle}
                  hint={mix.length ? `${mix.length}` : undefined}
                  data={mix}
                  total={detailCount}
                  emptyLabel={empty}
                />
              </StatsMixPanel>
            }
            chartsPlacement="right"
            tabs={[
              { id: 'summary', label: 'Counts', count: rows.length },
              { id: 'detail', label: meta.detailTab, count: detailCount },
            ]}
            activeTab={listTab}
            onTabChange={(id) => setListTab(id === 'detail' ? 'detail' : 'summary')}
          >
            <CrewDataTableFrame>
              {listTab === 'summary' ? (
                <DataTable
                  data={rows}
                  columns={columns}
                  keyExtractor={(row) => row.key}
                  emptyMessage={empty}
                  showFooter={rows.length > 0}
                />
              ) : focus === 'certs-expired' || focus === 'certs-expiring' ? (
                <DataTable
                  data={certs}
                  columns={certColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : (
                <DataTable
                  data={medicals}
                  columns={medicalColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              )}
            </CrewDataTableFrame>
          </StatsKpiListLayout>
        );
      }}
    </StatsPageShell>
  );
};

export default ComplianceCountStatisticsPage;
